<template>
    <section>
        <e-sidebar
            v-model="open"
            type="is-white"
            can-cancel
            fullheight
            right
            :fullwidth="$isMobile"
            :on-cancel="closeAlertDrawer"
            :class="['alert-drawer', $isMobile ? 'is-mobile' : '']"
        >
            <aside>
                <div class="is-flex-direction-column card-header">
                    <div class="level mb-0" :style="$isMobile ? 'display:flex;margin-bottom:0' : ''">
                        <div class="level-left">
                            <div class="level-item">
                                <h2 class="card-header-title title is-5 is-flex is-align-center">
                                    Notifications
                                    <div
                                        v-if="!isLoaded && notifications.length"
                                        class="notifications-counter is-relative is-flex is-align-center is-justify-content-center ml-3"
                                    >
                                        {{ notificationsCounter }}
                                    </div>
                                </h2>
                            </div>
                        </div>

                        <div class="level-right card-header-title">
                            <e-tooltip label="Mark All as Read" append-to-body position="is-left">
                                <e-button
                                    class="mr-2"
                                    icon-right="check-circle"
                                    aria-label="Mark All as Read"
                                    :disabled="!notifications.length"
                                    @click="markAllAsRead"
                                />
                            </e-tooltip>
                            <e-tooltip label="Close" append-to-body position="is-left">
                                <e-button
                                    icon-right="times"
                                    aria-label="Close"
                                    @click="closeAlertDrawer()"
                                />
                            </e-tooltip>
                        </div>
                    </div>
                </div>
                <div v-if="!isLoaded">
                    <alert-card
                        v-for="notification in notifications"
                        :key="notification.notification_id"
                        :notification="notification"
                        @update-notification="updateNotification"
                    />
                    <infinite-loading
                        v-if="hasNextPage && !infiniteIsLoading"
                        spinner="spiral"
                        @distance="4"
                        @infinite="handleLoadMore"
                    />
                </div>
                <div v-if="notifications.length === 0">
                    <p class="has-text-centered has-text-grey-light mt-5">No notifications to display.</p>
                </div>
            </aside>
        </e-sidebar>
    </section>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading';
import { mapState } from 'vuex';

import AlertCard from '@/components/alerts/AlertCard.vue';
import { handleResponseError } from '@/Helpers.js';
import BellNotification from '@/models/BellNotification.js';

const loadMoreRetryDelay = 2000;

// Keep the undo control while open, then remove only confirmed reads on close.
// Cleanup also runs while unrelated legacy writes or refresh requests are pending.
async function removeReadReminders(updates, hasNewerUpdate) {
    const completed = await Promise.all(
        updates.map(async update => ({
            ...update,
            succeeded: await update.pending,
        }))
    );
    const latest = new Map(completed.map(update => [update.notification_id, update]));
    const ids = [...latest.values()]
        .filter(update => update.succeeded && !hasNewerUpdate(update.notification_id))
        .map(update => update.notification_id);
    if (ids.length) {
        BellNotification.query()
            .findIn(ids)
            .filter(notification => notification.type === 'Deadline Reminder' && notification.read)
            .forEach(notification => BellNotification.delete(notification.notification_id));
    }
}

export default {
    name: 'AlertDrawer',

    components: { AlertCard, InfiniteLoading },
    emits: ['close-alert-drawer'],

    data: () => ({
        isLoaded: true,
        infiniteIsLoading: false,
        updatedNotifications: [],
        immediateUpdates: [],
    }),

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        open() {
            return this.$store.state.alertDrawer;
        },

        notificationsCounter() {
            return this.$store.state.entities.bell_notifications.counter;
        },

        notifications() {
            return BellNotification.query().orderBy('created_at', 'desc').get();
        },

        hasNextPage() {
            return (
                this.$store.state.entities.bell_notifications.page <
                this.$store.state.entities.bell_notifications.last_page
            );
        },

        currentPage: {
            get() {
                return this.$store.state.entities.bell_notifications.page;
            },

            set(value) {
                BellNotification.commit(state => (state.page = value));
            },
        },
    },

    async mounted() {
        this.isLoaded = false;
    },

    unmounted() {
        this.currentPage = 1;
    },

    methods: {
        updateNotification({ notification_id }, { immediate = false, pending } = {}) {
            if (immediate) {
                this.updatedNotifications = this.updatedNotifications.filter(id => id !== notification_id);
                this.immediateUpdates.push({
                    notification_id,
                    pending: Promise.resolve(pending).then(
                        () => true,
                        () => false
                    ),
                });
                return;
            }

            this.updatedNotifications = this.updatedNotifications.includes(notification_id)
                ? this.updatedNotifications.filter(id => id !== notification_id)
                : [...this.updatedNotifications, notification_id];
        },

        async closeAlertDrawer() {
            const { immediateUpdates } = this;
            this.immediateUpdates = [];
            const cleanup = removeReadReminders(immediateUpdates, id =>
                this.immediateUpdates.some(update => update.notification_id === id)
            );
            this.$emit('close-alert-drawer');
            this.$store.commit('SET_ALERT_DRAWER', { state: false });

            this.infiniteIsLoading = false;
            this.hasNextPage = true;

            if (this.updatedNotifications.length) {
                this.currentPage = 1;
                const notificationModels = BellNotification.query().findIn(this.updatedNotifications);
                await Promise.all(notificationModels.map(notification => notification.markAsRead()));
                notificationModels.forEach(notification => BellNotification.delete(notification.notification_id));
                await BellNotification.fetch(1);
                this.updatedNotifications = [];

                // Reload page 1 from the server now that the reads are committed.
                // fetch() sets `counter` from meta.total of the same query that
                // populates the drawer, so the badge is authoritative whether one,
                // many, or all notifications were marked read.
                await BellNotification.fetch();
            }

            await cleanup;
        },

        async fetchNotifications() {
            return BellNotification.api().get(`/api/notifications?page=${this.currentPage}`);
        },

        async handleLoadMore() {
            if (this.hasNextPage) {
                this.infiniteIsLoading = true;
                const page = this.currentPage + 1;
                this.currentPage = page;
                const response = await BellNotification.fetch(page);
                if (!response && this.currentPage === page) {
                    // Leave the page unloaded when it was discarded or failed so the loader retries it.
                    // Pause first so a persistent request failure does not retry in a tight loop.
                    this.currentPage = page - 1;
                    await new Promise(resolve => {
                        setTimeout(resolve, loadMoreRetryDelay);
                    });
                }
                this.infiniteIsLoading = false;
            }
        },

        async markAllAsRead() {
            const reminders = this.notifications.filter(notification => notification.type === 'Deadline Reminder');
            const ids = this.notifications
                .filter(notification => notification.type !== 'Deadline Reminder')
                .map(notification => notification.notification_id);
            const pending = reminders.map(notification => {
                const request = notification.markAsRead();
                this.updateNotification(notification, { immediate: true, pending: request });
                return request.catch(handleResponseError);
            });
            await BellNotification.update({
                where: notification => {
                    return ids.includes(notification.notification_id);
                },
                data: { read: true },
            });

            this.updatedNotifications = ids;
            if (pending.length) {
                await Promise.all(pending);
                await BellNotification.fetch();
            }
        },
    },
};
</script>
<style lang="scss">
@import '~assets/scss/toolbox';
.alert-drawer.p-drawer:not(.is-mobile) {
    width: 480px;
}
.alert-drawer .card-header {
    border-bottom: 1px solid $grey-lighter;
}
.notifications-counter {
    color: white;
    width: 22px;
    height: 22px;
    line-height: 0;
    font-size: 12px;
    border-radius: 50%;
    background-color: #ff5e73;
}
</style>
